const RULE_AGENT_BASE_URL = process.env.REACT_APP_RULE_AGENT_URL || "http://localhost:9001"

const RULE_AGENT_STREAM_TIMEOUT_MS = 45000

export const RESUMO_ALTERACAO_SENTINEL = "RESUMO_ALTERACAO:"

export async function openRuleAgentSession({ token, signal }) {
    let response = await fetch(RULE_AGENT_BASE_URL + "/session", {
        method: "POST",
        headers: {
            "Authorization": "Bearer " + token
        },
        signal
    })
    if (!response.ok) {
        throw new Error("Failed to open rule agent session: " + response.status)
    }
    let data = await response.json()
    return data.id
}

export function buildRuleChangePrompt({ oldRule, newRule }) {
    return (
        "Você está analisando uma alteração feita em uma regra do QI Tech Rule Engine.\n\n" +
        "Versão anterior da regra:\n" +
        "```json\n" +
        JSON.stringify(oldRule, null, 2) +
        "\n```\n\n" +
        "Versão atual da regra:\n" +
        "```json\n" +
        JSON.stringify(newRule, null, 2) +
        "\n```\n\n" +
        "Explique de forma clara e objetiva, em português, o que mudou entre as duas versões e qual o impacto dessa mudança no comportamento da regra.\n\n" +
        "Ao final da sua resposta, inclua obrigatoriamente uma última linha, exatamente no seguinte formato:\n" +
        RESUMO_ALTERACAO_SENTINEL + " <resumo da alteração em uma frase, adequado para um changelog>"
    )
}

export async function streamRuleChangeTurn({ sessionId, token, prompt, ruleContext, attachments, signal, onDelta, onRule, onDone, onError }) {
    let localController = new AbortController()
    let timedOut = false

    let forwardAbort = () => localController.abort()
    if (signal) {
        if (signal.aborted) localController.abort()
        else signal.addEventListener("abort", forwardAbort)
    }

    let timeoutId = setTimeout(() => {
        timedOut = true
        localController.abort()
    }, RULE_AGENT_STREAM_TIMEOUT_MS)

    let cleanup = () => {
        clearTimeout(timeoutId)
        if (signal) signal.removeEventListener("abort", forwardAbort)
    }

    let payload = { prompt }
    if (ruleContext) payload.rule_context = ruleContext
    if (attachments && attachments.length > 0) payload.attachments = attachments

    try {
        let response = await fetch(RULE_AGENT_BASE_URL + "/session/" + sessionId + "/turn/stream", {
            method: "POST",
            headers: {
                "Authorization": "Bearer " + token,
                "Content-Type": "application/json"
            },
            body: JSON.stringify(payload),
            signal: localController.signal
        })

        if (!response.ok || !response.body) {
            cleanup()
            onError && onError({ message: "Failed to start rule agent stream: " + response.status })
            return
        }

        let reader = response.body.getReader()
        let decoder = new TextDecoder()
        let buffer = ""

        while (true) {
            let { value, done } = await reader.read()
            if (done) break
            buffer += decoder.decode(value, { stream: true })

            let frames = buffer.split("\n\n")
            buffer = frames.pop()

            for (let frame of frames) {
                parseRuleAgentFrame(frame, { onDelta, onRule, onDone, onError })
            }
        }

        if (buffer.trim()) {
            parseRuleAgentFrame(buffer, { onDelta, onRule, onDone, onError })
        }
        cleanup()
    } catch (error) {
        cleanup()
        if (timedOut) {
            onError && onError({ message: "Rule agent stream timed out" })
            return
        }
        if (error.name === "AbortError") return
        onError && onError({ message: error.message })
    }
}

export const streamRuleAgentTurn = streamRuleChangeTurn

export function getRuleAgentDisplayText(fullText) {
    let sentinelIndex = fullText.indexOf(RESUMO_ALTERACAO_SENTINEL)
    if (sentinelIndex !== -1) {
        return fullText.slice(0, sentinelIndex).trim()
    }

    let maxOverlap = Math.min(RESUMO_ALTERACAO_SENTINEL.length - 1, fullText.length)
    for (let overlap = maxOverlap; overlap > 0; overlap--) {
        if (fullText.slice(-overlap) === RESUMO_ALTERACAO_SENTINEL.slice(0, overlap)) {
            return fullText.slice(0, fullText.length - overlap)
        }
    }

    return fullText
}

export function extractRuleChangeSummary(fullText) {
    let sentinelIndex = fullText.indexOf(RESUMO_ALTERACAO_SENTINEL)
    if (sentinelIndex === -1) return null
    let summary = fullText.slice(sentinelIndex + RESUMO_ALTERACAO_SENTINEL.length).trim()
    return summary.length > 0 ? summary : null
}

function parseRuleAgentFrame(frame, { onDelta, onRule, onDone, onError }) {
    let eventType = "message"
    let dataLines = []

    frame.split("\n").forEach((line) => {
        if (line.startsWith("event:")) {
            eventType = line.slice("event:".length).trim()
        } else if (line.startsWith("data:")) {
            dataLines.push(line.slice("data:".length).trim())
        }
    })

    if (dataLines.length === 0) return

    let rawData = dataLines.join("\n")
    let data
    try {
        data = JSON.parse(rawData)
    } catch (parseError) {
        console.error("Failed to parse rule agent SSE frame data:", rawData, parseError)
        return
    }

    if (eventType === "delta") {
        onDelta && onDelta(data)
    } else if (eventType === "rule") {
        onRule && onRule(data)
    } else if (eventType === "done") {
        onDone && onDone(data)
    } else if (eventType === "error") {
        onError && onError(data)
    }
}
